import { isInternal } from '../auth/roles.js';

/**
 * Gated /data/*.json fetch helpers for the Energy desk.
 *
 * Expected nginx auth_request denials (401/403) must not become PostHog
 * $exception events. Callers pass track sinks into reportDeskJsonFailure so
 * this module stays importable under plain Node tests (no Vite/posthog-js).
 */

/** Nginx /auth/verify denials for gated /data/*.json — must not become $exception. */
export function isExpectedAuthDenial(status) {
  return status === 401 || status === 403;
}

/**
 * The host publishes a deal's JSON after merge. A 404 before that step is
 * the empty state, not a crash and not an exception.
 */
export function isExpectedMissingDeskData(status) {
  return status === 404;
}

export const MISSING_DESK_DATA_MESSAGE =
  "This deal's data is not on this host yet. It is published after the data room is extracted.";

export function deskJsonHttpError(message, status) {
  const err = new Error(message);
  err.status = status;
  return err;
}

/**
 * True when a /auth/me-style payload can pull internal desk JSON.
 * Investors and incomplete auth (2FA / reset) are not usable for /data/*.
 */
export function sessionUsableForDesk(session) {
  if (!session) return false;
  const user = session.user || session;
  if (!user || typeof user !== 'object') return false;
  if (!isInternal(user)) return false;
  if (session.pending_2fa ?? user.pending_2fa) return false;
  if (session.needs_2fa_setup ?? user.needs_2fa_setup) return false;
  if (session.must_reset_password ?? user.must_reset_password) return false;
  return true;
}

/**
 * Single attempt to load gated desk JSON. Attaches HTTP status on non-ok.
 * @param {string} url
 * @param {{ signal?: AbortSignal, errorMessage?: string | ((status: number) => string) }} [opts]
 */
export async function fetchDeskJsonOnce(url, { signal, errorMessage } = {}) {
  const res = await fetch(url, { credentials: 'include', signal });
  if (!res.ok) {
    const msg =
      typeof errorMessage === 'function'
        ? errorMessage(res.status)
        : errorMessage || `${url.split('/').pop() || url} ${res.status}`;
    throw deskJsonHttpError(msg, res.status);
  }
  return res.json();
}

/**
 * Load gated /data JSON with at most one retry after refreshSession on 401/403.
 * Does not retry forever — second failure (or unusable session) propagates.
 *
 * @param {string} url
 * @param {{
 *   signal?: AbortSignal,
 *   refreshSession?: () => Promise<unknown>,
 *   errorMessage?: string | ((status: number) => string),
 * }} [opts]
 */
export async function fetchDeskJson(url, { signal, refreshSession, errorMessage } = {}) {
  try {
    return await fetchDeskJsonOnce(url, { signal, errorMessage });
  } catch (err) {
    if (!isExpectedAuthDenial(err?.status) || typeof refreshSession !== 'function') {
      throw err;
    }
    let session;
    try {
      session = await refreshSession();
    } catch {
      throw err;
    }
    if (!sessionUsableForDesk(session)) {
      throw err;
    }
    return fetchDeskJsonOnce(url, { signal, errorMessage });
  }
}

/**
 * Always emit a product event with HTTP status; captureException only for
 * unexpected failures (5xx, parse, network, missing status).
 *
 * @param {Error & { status?: number }} err
 * @param {{ event?: string, context?: string, dashboard?: string, resource?: string } & Record<string, unknown>} [props]
 * @param {{ track: Function, trackException: Function }} sinks
 */
export function reportDeskJsonFailure(err, props, sinks) {
  const capture = sinks?.track;
  const captureException = sinks?.trackException;
  if (typeof capture !== 'function') {
    throw new Error('reportDeskJsonFailure requires { track, trackException } sinks');
  }
  const { event = 'desk_json_load_failed', ...rest } = props || {};
  const status = err?.status;
  const expectedMissing = isExpectedMissingDeskData(status);
  capture(event, {
    ...rest,
    status: status ?? null,
    expected_auth_denial: isExpectedAuthDenial(status),
    expected_missing: expectedMissing,
  });
  if (!isExpectedAuthDenial(status) && !expectedMissing && typeof captureException === 'function') {
    captureException(err, rest);
  }
}
